import { useState, type ReactNode } from 'react';
import { Circle, MoveDownRight, Pencil, RectangleHorizontal, Ruler, Trash2 } from 'lucide-react';
import { PolySmoothIcon, PolyStraightIcon } from './polygonIcons';
import { cn } from '@/lib/utils';
import type { Annotation } from '../../annotation/types';
import {
  arrowLabel,
  colorForStyle,
  counterColor,
  counterLabel,
  formatArea,
  formatLength,
  isSmoothPolygon,
  measureLabel,
} from '../../annotation/style';
import { polygonArea, rectFrame, smoothClosedPoints } from '../../annotation/geometry';
import type { CounterSession } from './AnnotationOverlay';

interface Props {
  annotations: Annotation[];
  /** Active counting session — its live count overrides the saved one, and a
   *  brand-new group (id null) shows as an extra in-progress row. */
  session: CounterSession | null;
  pixelSpacingMm: number | null;
  onDelete: (id: string) => void;
  /** Click a counter to make it the active group (new dots append to it). */
  onActivateCounter: (a: Annotation) => void;
  /** Click any row to bring its annotation into view on the slide (user, 2026-10-10). */
  onShow?: (a: Annotation) => void;
  /** Set an annotation's display name (empty clears it). */
  onRename: (id: string, name: string) => void;
  /** List only: no rename, delete or resuming a counter, for somebody who may not annotate. */
  readOnly?: boolean;
  /** The annotation hovered, here or on the slide: its row shows hovered (user, 2026-10-10). */
  hoveredId?: string | null;
  /** The pointer has come onto a row, or left it (null). */
  onHover?: (id: string | null) => void;
}

/** Lists every annotation on the slide; the person's own have rename and
 *  delete, and their counters can be clicked to resume adding points to that
 *  group. Somebody else's are listed only: an annotation is its author's.
 *  Hovering a row hovers its annotation on the slide, which says who drew it
 *  and when there; clicking it brings the annotation into view. */
export function AnnotationsPanel({
  annotations,
  session,
  pixelSpacingMm,
  onDelete,
  onActivateCounter,
  onShow,
  onRename,
  readOnly = false,
  hoveredId = null,
  onHover,
}: Props) {
  const [editId, setEditId] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');
  /** May this person change it: they may annotate here, and they drew it. */
  const mayChange = (a: Annotation) => !readOnly && a.isYou === true;

  const measures = annotations.filter((a) => a.type === 'measure');
  const arrows = annotations.filter((a) => a.type === 'arrow');
  const shapes = annotations.filter((a) => a.type === 'rectangle' || a.type === 'ellipse');
  const polygons = annotations.filter((a) => a.type === 'polygon');
  const counters = annotations.filter((a) => a.type === 'counter');
  const empty =
    measures.length === 0 &&
    arrows.length === 0 &&
    shapes.length === 0 &&
    polygons.length === 0 &&
    counters.length === 0 &&
    !(session?.id === null && session.points.length > 0);

  const shapeArea = (a: Annotation) => {
    const f = rectFrame(a.points);
    const ar = a.type === 'ellipse' ? Math.PI * f.hx * f.hy : f.w * f.h;
    return formatArea(ar, pixelSpacingMm);
  };

  if (empty) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
        {readOnly ? 'No annotations on this slide.' : 'No annotations yet. Use the measure or counter tools above the viewport.'}
      </div>
    );
  }

  const startEdit = (a: Annotation) => {
    setEditId(a.id);
    setEditVal(a.text ?? '');
  };
  const commit = (id: string) => {
    onRename(id, editVal.trim());
    setEditId(null);
  };
  const nameField = (id: string, label: string) =>
    editId === id ? (
      <input
        autoFocus
        value={editVal}
        placeholder="Name"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setEditVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit(id);
          else if (e.key === 'Escape') setEditId(null);
        }}
        onBlur={() => commit(id)}
        className="min-w-0 flex-1 rounded border border-white/30 bg-transparent px-1 py-0.5 text-sm outline-none"
      />
    ) : (
      <span className="truncate">{label}</span>
    );

  /**
   * What every row does, whatever it lists: its half of the shared hover
   * (shown hovered, and telling the page when the pointer comes and goes), a
   * click that shows the annotation on the slide — and resumes one's own
   * counter — and rename and delete for what the person may change.
   */
  const rowProps = (a: Annotation) => {
    const resume = a.type === 'counter' && mayChange(a);
    return {
      hovered: hoveredId === a.id,
      onHover: (on: boolean) => onHover?.(on ? a.id : null),
      onClick: !onShow && !resume ? undefined : () => {
        onShow?.(a);
        if (resume) onActivateCounter(a);
      },
      editing: editId === a.id,
      onRename: mayChange(a) ? () => startEdit(a) : undefined,
      onDelete: mayChange(a) ? () => onDelete(a.id) : undefined,
    };
  };

  return (
    <div className="flex flex-col gap-4 p-3">
      {measures.length > 0 && (
        <Section title="Measurements">
          {measures.map((a) => {
            const len = formatLength(
              Math.hypot(a.points[1].x - a.points[0].x, a.points[1].y - a.points[0].y),
              pixelSpacingMm,
            );
            return (
              <Row
                key={a.id}
                {...rowProps(a)}
              >
                <Ruler className="h-4 w-4 shrink-0" style={{ color: colorForStyle(a.style) }} />
                {nameField(a.id, measureLabel(a.text, len))}
              </Row>
            );
          })}
        </Section>
      )}

      {arrows.length > 0 && (
        <Section title="Arrows">
          {arrows.map((a) => (
            <Row
              key={a.id}
              {...rowProps(a)}
            >
              <MoveDownRight className="h-4 w-4 shrink-0" style={{ color: colorForStyle(a.style) }} />
              {nameField(a.id, arrowLabel(a.text))}
            </Row>
          ))}
        </Section>
      )}

      {shapes.length > 0 && (
        <Section title="Shapes">
          {shapes.map((a) => (
            <Row
              key={a.id}
              {...rowProps(a)}
            >
              {a.type === 'ellipse' ? (
                <Circle className="h-4 w-4 shrink-0" style={{ color: colorForStyle(a.style) }} />
              ) : (
                <RectangleHorizontal
                  className="h-4 w-4 shrink-0"
                  style={{ color: colorForStyle(a.style) }}
                />
              )}
              {nameField(a.id, measureLabel(a.text, shapeArea(a)))}
            </Row>
          ))}
        </Section>
      )}

      {polygons.length > 0 && (
        <Section title="Regions">
          {polygons.map((a) => {
            const smooth = isSmoothPolygon(a.style);
            // Match the overlay: area from the drawn outline (smoothed or straight).
            const outline = smooth ? smoothClosedPoints(a.points) : a.points;
            const Icon = smooth ? PolySmoothIcon : PolyStraightIcon;
            return (
              <Row
                key={a.id}
                {...rowProps(a)}
              >
                <Icon className="h-4 w-4 shrink-0" style={{ color: colorForStyle(a.style) }} />
                {nameField(a.id, measureLabel(a.text, formatArea(polygonArea(outline), pixelSpacingMm)))}
              </Row>
            );
          })}
        </Section>
      )}

      {(counters.length > 0 || (session?.id === null && session.points.length > 0)) && (
        <Section title="Counters">
          {counters.map((a) => {
            const active = session?.id === a.id;
            const count = active ? session!.points.length : a.points.length;
            return (
              <Row
                key={a.id}
                {...rowProps(a)}
                active={active}
              >
                <Swatch color={counterColor(a.style)} />
                {nameField(a.id, counterLabel(a.text, count))}
              </Row>
            );
          })}
          {session && session.id === null && session.points.length > 0 && (
            <Row active>
              <Swatch color={counterColor(`counter:${session.colorIndex}`)} />
              <span className="truncate">{counterLabel(undefined, session.points.length)}</span>
            </Row>
          )}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

function Row({
  children,
  active,
  hovered,
  onHover,
  editing,
  onClick,
  onRename,
  onDelete,
}: {
  children: ReactNode;
  active?: boolean;
  /** Shown hovered though the pointer may be on the slide, over its annotation. */
  hovered?: boolean;
  onHover?: (on: boolean) => void;
  editing?: boolean;
  onClick?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}) {
  // The row's controls show on hover, the pointer's or the slide's.
  const reveal = hovered ? 'opacity-100' : 'opacity-0 group-hover:opacity-100';
  return (
    <div
      className={cn(
        'group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
        onClick && 'cursor-pointer',
        active ? 'bg-accent text-foreground' : hovered ? 'bg-accent/50' : 'hover:bg-accent/50',
      )}
      onClick={onClick}
      onMouseEnter={onHover && (() => onHover(true))}
      onMouseLeave={onHover && (() => onHover(false))}
    >
      {children}
      <div className="ml-auto flex shrink-0 items-center">
        {onRename && !editing && (
          <button
            type="button"
            className={cn('inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-opacity hover:text-foreground', reveal)}
            onClick={(e) => {
              e.stopPropagation();
              onRename();
            }}
            title="Rename"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            className={cn('inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-opacity hover:text-destructive', reveal)}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function Swatch({ color }: { color: string }) {
  return (
    <span
      className="h-3 w-3 shrink-0 rounded-full border border-black/40"
      style={{ backgroundColor: color }}
    />
  );
}
